/**
 * NavBadge — small pill showing an unread/pending count next to a nav item.
 *
 * NS4 Round 3 R3.1. Renders nothing when count is 0 or null. Color
 * tinted by urgency tier so the user reads severity at a glance:
 *   - 'info'    (default) — primary teal — informational counts
 *   - 'warn'    — amber  — needs-attention soon
 *   - 'urgent'  — red    — overdue / blocking
 *
 * Usage:
 *   <NavBadge count={pendingApprovals} variant="urgent" />
 *   <NavBadge count={notificationsUnread} />  // defaults to info/teal
 */

interface NavBadgeProps {
  count: number | null | undefined;
  variant?: 'info' | 'warn' | 'urgent';
  /** When count exceeds this, render "99+". Defaults to 99. */
  cap?: number;
  /** Optional aria-label override; default reads "{count} {variant}". */
  ariaLabel?: string;
}

export function NavBadge({ count, variant = 'info', cap = 99, ariaLabel }: NavBadgeProps) {
  if (!count || count <= 0) return null;
  const displayed = count > cap ? `${cap}+` : String(count);

  const tints: Record<NonNullable<NavBadgeProps['variant']>, string> = {
    info:   'bg-primary-500 text-white',
    warn:   'bg-amber-500 text-white',
    urgent: 'bg-red-500 text-white',
  };

  return (
    <span
      className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold rounded-full ${tints[variant]} shrink-0`}
      aria-label={ariaLabel ?? `${count} ${variant}`}
    >
      {displayed}
    </span>
  );
}
